/**
 * useTimeFormat — small hook that exposes a persisted 12h/24h preference.
 *
 * Default detection: read `Intl.DateTimeFormat().resolvedOptions().hour12`
 * for the user's locale. The user can override via the toggle in the booking
 * UI; the choice persists in localStorage so subsequent visits respect it.
 *
 * The hook also returns a formatter that produces "2:30 PM" / "14:30" style
 * strings consistently across the app — keep callers off `Intl` directly so
 * the format choice stays centralised.
 */

import { useCallback, useEffect, useState } from 'react';

import { type TimeFormat, timeParts } from '@/lib/time/timeFormat';

export type { TimeFormat };

const STORAGE_KEY = 'ahoy.booking.timeFormat';

function detectDefault(): TimeFormat {
  if (typeof Intl === 'undefined') {
    return '12h';
  }
  try {
    // `resolvedOptions().hour12` is only populated when the formatter was
    // constructed with an hour-bearing field (or `hourCycle`/`hour12`).
    // A bare `new Intl.DateTimeFormat()` resolves to a *date-only* formatter,
    // so `hour12` is `undefined` and our `hour12 === false ? '24h' : '12h'`
    // branch silently falls into the `12h` default — wrong for locales like
    // `en-GB`, `de-DE`, `fr-FR`, etc. that default to 24h.
    //
    // Construct the formatter with `hour: 'numeric'` so the runtime actually
    // resolves the cycle from the host's locale-and-region pair. Some
    // implementations (older Safari) leave `hour12` undefined even with the
    // hour field; fall back to inspecting `hourCycle` ('h11'/'h12' → 12h,
    // 'h23'/'h24' → 24h) so we don't misclassify those locales either.
    const resolved = new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
    }).resolvedOptions();
    if (typeof resolved.hour12 === 'boolean') {
      return resolved.hour12 ? '12h' : '24h';
    }
    const cycle = resolved.hourCycle;
    if (cycle === 'h23' || cycle === 'h24') {
      return '24h';
    }
    if (cycle === 'h11' || cycle === 'h12') {
      return '12h';
    }
    return '12h';
  } catch {
    return '12h';
  }
}

function readStoredValue(): TimeFormat | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === '12h' || raw === '24h') {
      return raw;
    }
  } catch {
    // localStorage can throw in privacy modes / sandboxed iframes — fall
    // through to detection.
  }
  return null;
}

export function useTimeFormat(): {
  format: TimeFormat;
  setFormat: (next: TimeFormat) => void;
  formatTime: (isoOrDate: string | Date, timezone: string) => string;
} {
  const [format, setFormatState] = useState<TimeFormat>(() => {
    return readStoredValue() ?? detectDefault();
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, format);
    } catch {
      // ignore — non-fatal
    }
  }, [format]);

  const setFormat = useCallback((next: TimeFormat) => {
    setFormatState(next);
  }, []);

  const formatTime = useCallback(
    (isoOrDate: string | Date, timezone: string) => {
      const date =
        typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
      return new Intl.DateTimeFormat(undefined, {
        ...timeParts(format),
        timeZone: timezone,
      }).format(date);
    },
    [format]
  );

  return { format, setFormat, formatTime };
}
